import { useTranslation } from 'react-i18next';
import type { PageType } from 'types';
import type { UpcomingProductsModule } from 'types/capi/modules';

import NotificationTooltip from '~/components/NotificationTooltip';
import ProductCard from '~/components/ProductCard';
import Tracked from '~/components/Tracked';
import useRegion from '~/hooks/useRegion';

type Props = { items: UpcomingProductsModule['items']; pageType: PageType };

function Products({ items, pageType }: Props) {
  const region = useRegion();

  const allowNotifications = region.code === 'GB';

  return (
    <div className="custom-scrollbar flex overflow-x-scroll md:block md:overflow-x-auto">
      <div className="grid shrink-0 grid-cols-6 gap-2 md:grid-cols-3 md:gap-6">
        {items.map((product, index) => (
          <ProductCard.Internal
            key={`upcoming-product-${product.id}`}
            product={product}
            padImage
            className="h-auto w-[285px] md:w-full"
            allowNotifications={allowNotifications}
            imageLoading="eager"
            cardSize="small"
            showNotificationCount={true}
            position={index + 1}
            pageType={pageType}
            pagePosition="Homepage Upcoming Releases"
            overrideInteractionPagePosition={true}
          />
        ))}
      </div>
    </div>
  );
}

function ViewMoreButton() {
  const { t } = useTranslation('common');

  return (
    <div className="w-36">
      <Tracked.InternalLink
        variant="transparent-black-border"
        className="flex items-center justify-center"
        pagePosition="Homepage Upcoming Releases"
        name="VIEW_MORE"
        to={`/release-dates/?sort=UPCOMING_RELEASES`}
      >
        {t('viewMore')}
      </Tracked.InternalLink>
    </div>
  );
}

export default function UpcomingProducts({ items, pageType }: Props) {
  const region = useRegion();
  const { t } = useTranslation('home');

  return (
    <div className="w-full bg-grey-tile">
      <div className="flex flex-col items-center justify-center gap-6 px-4 py-10 md:mx-auto md:max-w-6xl md:gap-10 md:py-10">
        <div className="flex flex-col justify-center gap-6 self-stretch py-0 lg:w-full">
          <div>
            <h2 className="justify-between gap-2 text-[32px] leading-10 md:max-w-6xl md:text-[40px] md:leading-[48px]">
              {t('upcomingReleases')}
            </h2>
            <div className="flex w-full flex-col gap-2 text-base md:flex-row md:justify-between md:text-lg">
              <p className="md:self-end">{t('upcomingDescription')}</p>
              <div className="flex md:justify-end">
                {region.code === 'GB' && (
                  <NotificationTooltip className="min-w-[285px] md:min-w-[358px]" />
                )}
              </div>
            </div>
          </div>
          <Products items={items} pageType={pageType} />
        </div>
        <ViewMoreButton />
      </div>
    </div>
  );
}
